import React, { FC, ReactNode, useState } from 'react';
import { Spin } from 'antd';
import moment from 'moment-timezone';
import {
  BookableDay, formatSlotTime, getTimeSegment, groupSlotsBySegment, TimeSegment,
} from '../../utils/clinician-availability';
import { getMonthBounds, MONTH_KEY } from './TimePicker';

type SectionProps = {
  index: number;
  label: string;
  value?: string | null;
  placeholder: string;
  detail?: string | null;
  open: boolean;
  done: boolean;
  onOpen: () => void;
  children: ReactNode;
};

// One numbered step of the appointment on phones, as in the ADHD portal: open while being
// chosen, then collapsed to its value with a tick and "Change".
export const MobileSection: FC<SectionProps> = ({
  index, label, value, placeholder, detail, open, done, onOpen, children,
}) => (
  <section className={`msec${open ? ' active' : ''}${done ? ' done' : ''}`}>
    <button type="button" className="msec-hd" aria-expanded={open} onClick={onOpen}>
      <span className="msec-idx" aria-hidden>{done && !open ? '✓' : index}</span>
      <span className="msec-tx">
        <span className="msec-k">{label}</span>
        <span className={`msec-v${value ? '' : ' unset'}`}>{value || placeholder}</span>
        {detail && <span className="msec-v2">{detail}</span>}
      </span>
      {done && !open && <span className="msec-act">Change</span>}
    </button>
    {open && <div className="msec-bd">{children}</div>}
  </section>
);

type DateStripProps = {
  month: string; // YYYY-MM
  // eslint-disable-next-line no-unused-vars
  onMonthChange: (month: string) => void;
  days: BookableDay[];
  isLoading: boolean;
  date?: string;
  // eslint-disable-next-line no-unused-vars
  onSelectDate: (date: string) => void;
  timezone: string;
};

// The bookable days of a month as a scrolling row, as in the ADHD portal.
export const DateStrip: FC<DateStripProps> = ({
  month, onMonthChange, days, isLoading, date, onSelectDate, timezone,
}) => {
  const { firstMonth, lastMonth } = getMonthBounds(timezone);
  const monthStart = moment(month, MONTH_KEY);
  const shiftMonth = (offset: number) => onMonthChange(monthStart.clone().add(offset, 'month').format(MONTH_KEY));

  return (
    <Spin spinning={isLoading}>
      <div className="mmonth">
        <button type="button" aria-label="Previous month" disabled={month <= firstMonth} onClick={() => shiftMonth(-1)}>‹</button>
        <b>{monthStart.format('MMMM YYYY')}</b>
        <button type="button" aria-label="Next month" disabled={month >= lastMonth} onClick={() => shiftMonth(1)}>›</button>
      </div>
      {days.length ? (
        <div className="dstrip">
          {days.map(({ date: day }) => {
            const m = moment(day);
            return (
              <button
                key={day}
                type="button"
                className={`dchip${day === date ? ' on' : ''}`}
                aria-pressed={day === date}
                aria-label={m.format('dddd D MMMM')}
                onClick={() => onSelectDate(day)}
              >
                <small>{m.format('ddd')}</small>
                <b>{m.date()}</b>
                <i aria-hidden />
              </button>
            );
          })}
        </div>
      ) : !isLoading && <div className="empty">{`No appointments in ${monthStart.format('MMMM')}. Try the next month.`}</div>}
    </Spin>
  );
};

type SegmentedSlotsProps = {
  day: BookableDay;
  startTime?: string;
  // eslint-disable-next-line no-unused-vars
  onSelectTime: (startTime: string) => void;
};

// A day's times split into Morning / Afternoon / Evening tabs, as in the ADHD portal.
// Opens on the chosen time's part of the day, otherwise the first with times.
export const SegmentedSlots: FC<SegmentedSlotsProps> = ({ day, startTime, onSelectTime }) => {
  const groups = groupSlotsBySegment(day.slots);
  const [picked, setPicked] = useState<TimeSegment | null>(null);
  const preferred = picked ?? (startTime ? getTimeSegment(startTime) : null);
  const current = groups.find(({ key }) => key === preferred) ?? groups[0];

  if (!current) return null;

  return (
    <>
      {groups.length > 1 && (
        <div className="segtabs" role="tablist" aria-label="Part of the day">
          {groups.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={key === current.key}
              className={key === current.key ? 'on' : ''}
              onClick={() => setPicked(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <div className="slots slots-3" role="tabpanel">
        {current.slots.map(({ start }) => (
          <button
            key={start}
            type="button"
            className={start === startTime ? 'on' : ''}
            aria-pressed={start === startTime}
            onClick={() => onSelectTime(start)}
          >
            {formatSlotTime(start)}
          </button>
        ))}
      </div>
    </>
  );
};
